/**
 * pages/ComptesADV2.js
 * The Crowd v2 — Gestion utilisateurs › Comptes AD
 *
 * Copie v2 de pages/GestionComptesAD.js. Changement visuel uniquement :
 * les quatre sous-composants (DemandesOdoo, NouvelleDemande,
 * SuiviDemandes, AdminReferentiels) sont repris a l'identique — memes
 * appels API, meme logique, memes classes ad-* — et redistribues en
 * trois pages, ouvertes depuis la carte Comptes AD :
 *
 *   Creation   -> onglets Odoo et Nouvelle demande
 *   Historique -> Demandes (suivi, etapes, journal)
 *   Gestion    -> Administration des referentiels (directeurs)
 *
 * Seuls changent, dans le code repris : les classes globales de carte
 * et de bouton (remplacees par leurs equivalents v2) et les symboles en
 * tete des messages d'erreur et de succes (la couleur suffit). Les
 * styles ad-* sont redefinis pour la v2 dans view/v2-comptes-ad.css.
 *
 * L'interface actuelle (GestionComptesAD.js) est destinee a etre
 * debranchee : les evolutions se font desormais ici.
 */

import { useState, useEffect, useCallback } from "react";
import {
  getReferentielsAD,
  getOUCiblesAD,
  simulerCompteAD,
  creerDemandeAD,
  getDemandesAD,
  getDemandeAD,
  validerDemandeAD,
  annulerDemandeAD,
  getDemandesOdoo,
  getFicheOdoo,
  getTablesAdminAD,
  getOptionsAdminAD,
  getValeursAdminAD,
  getLignesAdminAD,
  getValeursFiltreAdminAD,
  getHistoriqueAdminAD,
  creerLigneAdminAD,
  modifierLigneAdminAD,
  desactiverLigneAdminAD,
  reactiverLigneAdminAD,
} from "../services/api";

const FORM_VIDE = {
  prenom: "",
  nom: "",
  entite: "",
  ou_dn: "",
  type_personnel: "Salarie",
  contrat: "CDI",
  est_alternant: false,
  etablissement: "",
  projet: "",
  est_agence: null,
  nom_langue_locale: "",
  langue_poste: "FR",
  company: "",
  department: "",
  title: "",
  manager_upn: "",
  telephone_teams: "",
  mobile_perso: "",
  description: "",
  employee_id: "",
  date_expiration: "",
  mdp_destinataire_upn: "",
};

const LIBELLE_STATUT = {
  en_attente_validation: "À valider",
  en_attente: "En attente",
  en_cours: "En cours",
  termine: "Terminé",
  erreur: "Erreur",
  annule: "Annulé",
};

const LIBELLE_ETAPE = {
  controles_prealables: "Contrôles préalables",
  creation_ad: "Création du compte AD",
  groupes_ad: "Groupes AD on-premises",
  adsync: "Synchronisation Entra",
  attente_entra: "Attente de propagation",
  groupes_entra: "Groupes Entra et licence",
  attente_licence: "Attente de provisioning",
  listes_diffusion: "Listes de diffusion",
  notification: "Notification",
};

const LIBELLE_EXECUTEUR = {
  backend: "TheCrowd",
  ad: "Active Directory",
  graph: "Entra / Graph",
  exchange: "Exchange Online",
};

// ---------------------------------------------------------------------------
// Onglet : nouvelle demande
// ---------------------------------------------------------------------------

function NouvelleDemande({ collaborateur, referentiels, onCree }) {
  const [form, setForm] = useState(FORM_VIDE);
  const [ous, setOus] = useState([]);
  const [calcul, setCalcul] = useState(null);
  const [groupesCoches, setGroupesCoches] = useState({});
  // Identifiants retenus : initialises par le calcul, modifiables ensuite.
  const [identifiants, setIdentifiants] = useState(null);
  const [calculEnCours, setCalculEnCours] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  const estFreelance = form.type_personnel === "Freelance";

  const libelleOU = (o) => o.libelle;

  const ousDeLEntite = form.entite ? ous.filter((o) => o.entite === form.entite) : [];
  const ousAutres = form.entite ? ous.filter((o) => o.entite !== form.entite) : ous;

  // Toutes les OU sont chargees une fois. L'emplacement et le rattachement
  // sont independants : changer d'entite ne restreint pas les OU accessibles.
  useEffect(() => {
    getOUCiblesAD()
      .then(setOus)
      .catch(() => setOus([]));
  }, []);

  // Toute modification du formulaire invalide le calcul precedent :
  // afficher un resultat obsolete serait pire que de ne rien afficher.
  const majChamp = (nom, valeur) => {
    setForm((f) => {
      const suivant = { ...f, [nom]: valeur };

      // Choisir une OU propose l'entite qui lui est habituellement associee.
      // Proposition seulement : l'entite reste modifiable ensuite, et
      // changer d'entite ne deplace pas le compte.
      if (nom === "ou_dn" && valeur) {
        const ou = ous.find((o) => o.ou_dn === valeur);
        if (ou && ou.entite) suivant.entite = ou.entite;
      }

      // Le contrat determine le type de personnel : c'est lui que porte le
      // referentiel Odoo, le type n'en est qu'une consequence.
      if (nom === "contrat") {
        const c = (referentiels.contrats || []).find((x) => x.libelle === valeur);
        if (c) suivant.type_personnel = c.type_personnel;
      }
      if (nom === "type_personnel" && valeur === "Freelance") {
        suivant.contrat = "Freelance";
      }

      // est_agence est pre-rempli depuis l'etablissement, puis ajustable.
      // Sans etablissement connu, on laisse null plutot que false : le backend
      // retiendra alors la valeur de Ref_Etablissements s'il en trouve une.
      if (nom === "etablissement") {
        const etab = (referentiels.etablissements || []).find((e) => e.nom === valeur);
        suivant.est_agence = etab ? !!etab.est_agence : null;
      }
      return suivant;
    });
    setCalcul(null);
    setIdentifiants(null);
    setSucces(null);
    setErreur(null);
  };

  const lancerCalcul = async () => {
    if (!form.prenom.trim() || !form.nom.trim() || !form.entite) {
      setErreur("Prénom, nom et entité sont obligatoires.");
      return;
    }
    if (estFreelance && !form.projet.trim()) {
      setErreur("Le nom du projet est obligatoire pour un freelance : il entre dans l'adresse mail.");
      return;
    }
    setCalculEnCours(true);
    setErreur(null);
    try {
      const res = await simulerCompteAD(
        {
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          entite: form.entite,
          ou_dn: form.ou_dn || null,
          type_personnel: form.type_personnel,
          contrat: form.contrat,
          est_alternant: form.est_alternant,
          etablissement: form.etablissement || null,
          projet: form.projet.trim() || null,
          est_agence: form.est_agence,
          nom_langue_locale: form.nom_langue_locale.trim() || null,
          langue_poste: form.langue_poste,
        },
        collaborateur.id
      );
      setCalcul(res);
      const coches = {};
      (res.groupes || []).forEach((g) => {
        coches[g.nom] = g.coche;
      });
      setGroupesCoches(coches);
      setIdentifiants({
        sam_account_name: res.nommage.sam_account_name,
        upn: res.nommage.upn,
        mail_principal: res.nommage.mail_principal,
        proxy_addresses: [...(res.nommage.proxy_addresses || [])],
      });
    } catch (e) {
      setErreur(e.message || "Erreur lors du calcul.");
      setCalcul(null);
    } finally {
      setCalculEnCours(false);
    }
  };

  const majIdentifiant = (champ, valeur) => {
    setIdentifiants((i) => ({ ...i, [champ]: valeur }));
    setSucces(null);
  };

  const majProxy = (index, valeur) => {
    setIdentifiants((i) => {
      const liste = [...(i.proxy_addresses || [])];
      liste[index] = valeur;
      return { ...i, proxy_addresses: liste };
    });
    setSucces(null);
  };

  // Signale visuellement qu'un identifiant a ete modifie par rapport au calcul
  const ecartIdentifiant = (champ) => {
    if (!calcul || !identifiants) return null;
    const cle = champ === "mail_principal" ? "mail_principal" : champ;
    const calcule = calcul.nommage[cle];
    const retenu = identifiants[champ];
    if (!calcule || !retenu || calcule.toLowerCase() === retenu.toLowerCase()) return null;
    return <p className="ad-aide">Calculé : {calcule}</p>;
  };

  const basculerGroupe = (groupe) => {
    if (!groupe.ajustable) return;
    setGroupesCoches((c) => ({ ...c, [groupe.nom]: !c[groupe.nom] }));
  };

  const enregistrer = async () => {
    if (!calcul) return;
    setEnvoiEnCours(true);
    setErreur(null);
    try {
      const retenus = Object.keys(groupesCoches).filter((n) => groupesCoches[n]);
      const res = await creerDemandeAD({
        ...form,
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        ou_dn: form.ou_dn || null,
        etablissement: form.etablissement || null,
        projet: form.projet.trim() || null,
        date_expiration: form.date_expiration || null,
        groupes_retenus: retenus,
        sam_account_name: identifiants?.sam_account_name || null,
        upn: identifiants?.upn || null,
        mail_principal: identifiants?.mail_principal || null,
        proxy_addresses: identifiants?.proxy_addresses || null,
        source: "interface",
        collaborateur_id: collaborateur.id,
      });
      setSucces(
        `Demande #${res.id} enregistrée — ${res.sam_account_name}. ` +
          `Le compte sera créé au prochain passage de l'agent.`
      );
      setForm(FORM_VIDE);
      setCalcul(null);
      setIdentifiants(null);
      setGroupesCoches({});
      if (onCree) onCree();
    } catch (e) {
      setErreur(e.message || "Erreur lors de l'enregistrement.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const groupesParEtape = (calcul?.groupes || []).reduce((acc, g) => {
    (acc[g.etape] = acc[g.etape] || []).push(g);
    return acc;
  }, {});

  return (
    <div className="ad-colonnes">
      {/* ---------------- Formulaire ---------------- */}
      <div className="v2-panneau ad-formulaire">
        <h3 className="ad-section-titre">Identité</h3>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Prénom *</label>
            <input value={form.prenom} onChange={(e) => majChamp("prenom", e.target.value)}
                   placeholder="Marie" autoComplete="off" />
          </div>
          <div className="ad-champ">
            <label className="ad-label">Nom *</label>
            <input value={form.nom} onChange={(e) => majChamp("nom", e.target.value)}
                   placeholder="Lefevre" autoComplete="off" />
          </div>
        </div>

        <h3 className="ad-section-titre">Rattachement</h3>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Entité *</label>
            <select value={form.entite} onChange={(e) => majChamp("entite", e.target.value)}>
              <option value="">— Choisir —</option>
              {(referentiels.entites || []).map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>
          <div className="ad-champ">
            <label className="ad-label">Type *</label>
            <select value={form.type_personnel}
                    onChange={(e) => majChamp("type_personnel", e.target.value)}>
              {(referentiels.types_personnel || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {ous.length > 0 && (
          <div className="ad-champ">
            <label className="ad-label">OU cible</label>
            <select value={form.ou_dn} onChange={(e) => majChamp("ou_dn", e.target.value)}>
              <option value="">— OU par défaut de l'entité —</option>
              {form.entite && ousDeLEntite.length > 0 && (
                <optgroup label={form.entite}>
                  {ousDeLEntite.map((o) => (
                    <option key={o.ou_dn} value={o.ou_dn}>{libelleOU(o)}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label={form.entite ? "Autres entités" : "Toutes les OU"}>
                {ousAutres.map((o) => (
                  <option key={o.ou_dn} value={o.ou_dn}>
                    {libelleOU(o)}{form.entite ? ` — ${o.entite}` : ""}
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="ad-aide">
              Emplacement du compte. Indépendant de l'entité, qui gouverne les adresses.
            </p>
          </div>
        )}

        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Contrat *</label>
            <select value={form.contrat} onChange={(e) => majChamp("contrat", e.target.value)}>
              {(referentiels.contrats || []).map((c) => (
                <option key={c.libelle} value={c.libelle}>{c.libelle}</option>
              ))}
            </select>
          </div>
          <div className="ad-champ">
            <label className="ad-label">Établissement</label>
            <select value={form.etablissement}
                    onChange={(e) => majChamp("etablissement", e.target.value)}>
              <option value="">— Choisir —</option>
              {(referentiels.etablissements || []).map((e) => (
                <option key={e.nom} value={e.nom}>
                  {e.nom}{e.ville ? ` — ${e.ville}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {estFreelance && (
          <div className="ad-champ">
            <label className="ad-label">Projet *</label>
            <input value={form.projet} onChange={(e) => majChamp("projet", e.target.value)}
                   placeholder="EventX" autoComplete="off" />
            <p className="ad-aide">
              Entre dans l'adresse mail (prefixe-projet@…), jamais dans le login ni l'UPN.
            </p>
          </div>
        )}

        <div className="ad-cases">
          {!estFreelance && (
            <label className="ad-case">
              <input type="checkbox" checked={form.est_alternant}
                     onChange={(e) => majChamp("est_alternant", e.target.checked)} />
              Alternant
              <span className="ad-aide-inline">
                Le contrat reste CDD ; cette case conserve l'information séparément.
              </span>
            </label>
          )}
          <label className="ad-case">
            <input type="checkbox" checked={!!form.est_agence}
                   onChange={(e) => majChamp("est_agence", e.target.checked)} />
            Présent à l'agence
            <span className="ad-aide-inline">
              Pré-coché d'après l'établissement — ajustable.
            </span>
          </label>
        </div>

        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Langue du poste</label>
            <select value={form.langue_poste}
                    onChange={(e) => majChamp("langue_poste", e.target.value)}>
              {(referentiels.langues_poste || []).map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="ad-champ">
            <label className="ad-label">Nom en langue locale</label>
            <input value={form.nom_langue_locale}
                   onChange={(e) => majChamp("nom_langue_locale", e.target.value)}
                   placeholder="Chine, Japon, Corée uniquement" autoComplete="off" />
          </div>
        </div>

        <h3 className="ad-section-titre">Organisation</h3>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Société contractuelle</label>
            <select value={form.company} onChange={(e) => majChamp("company", e.target.value)}>
              <option value="">— Choisir —</option>
              {(referentiels.societes || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="ad-champ">
            <label className="ad-label">Pôle analytique</label>
            <select value={form.department}
                    onChange={(e) => majChamp("department", e.target.value)}>
              <option value="">— Choisir —</option>
              {(referentiels.departements || []).map((d) => (
                <option key={d.code} value={d.libelle}>{d.libelle}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Fonction</label>
            <input value={form.title} onChange={(e) => majChamp("title", e.target.value)}
                   autoComplete="off" />
          </div>
          <div className="ad-champ">
            <label className="ad-label">Manager (UPN)</label>
            <input value={form.manager_upn}
                   onChange={(e) => majChamp("manager_upn", e.target.value)}
                   placeholder="prenom@hopscotchgroupe.com" autoComplete="off" />
          </div>
        </div>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Téléphone Teams</label>
            <input value={form.telephone_teams}
                   onChange={(e) => majChamp("telephone_teams", e.target.value)}
                   autoComplete="off" />
          </div>
          <div className="ad-champ">
            <label className="ad-label">Mobile personnel</label>
            <input value={form.mobile_perso}
                   onChange={(e) => majChamp("mobile_perso", e.target.value)}
                   autoComplete="off" />
          </div>
        </div>
        <div className="ad-grille-2">
          <div className="ad-champ">
            <label className="ad-label">Matricule Odoo</label>
            <input value={form.employee_id}
                   onChange={(e) => majChamp("employee_id", e.target.value)}
                   placeholder="Laisser vide si la fiche Odoo n'existe pas" autoComplete="off" />
          </div>
          <div className="ad-champ">
            <label className="ad-label">Fin de contrat</label>
            <input type="date" value={form.date_expiration}
                   onChange={(e) => majChamp("date_expiration", e.target.value)} />
            <p className="ad-aide">Expiration du compte — laisser vide pour un CDI.</p>
          </div>
        </div>

        <div className="ad-champ">
          <label className="ad-label">Mot de passe envoyé à (UPN)</label>
          <input value={form.mdp_destinataire_upn}
                 onChange={(e) => majChamp("mdp_destinataire_upn", e.target.value)}
                 placeholder="Technicien qui prépare le poste" autoComplete="off" />
        </div>

        {erreur && <div className="ad-message ad-message-erreur">{erreur}</div>}
        {succes && <div className="ad-message ad-message-succes">{succes}</div>}

        <div className="ad-actions">
          <button className="v2-btn v2-btn-secondaire" onClick={lancerCalcul} disabled={calculEnCours}>
            {calculEnCours ? "Calcul en cours…" : "Calculer"}
          </button>
          <button className="v2-btn v2-btn-principal" onClick={enregistrer}
                  disabled={!calcul || envoiEnCours}>
            {envoiEnCours ? "Enregistrement…" : "Enregistrer la demande"}
          </button>
        </div>
      </div>

      {/* ---------------- Resultat du calcul ---------------- */}
      <div className="ad-resultat">
        {!calcul && (
          <div className="v2-panneau ad-vide">
            Renseigne le formulaire puis lance le calcul pour voir les identifiants,
            les groupes et les attributs qui seront appliqués.
          </div>
        )}

        {calcul && (
          <>
            <div className="v2-panneau">
              <h3 className="ad-section-titre">Identifiants</h3>
              <p className="ad-aide">
                Calculés par le moteur, modifiables. L'unicité et le format sont
                revérifiés à l'enregistrement.
              </p>

              <div className="ad-champ">
                <label className="ad-label">Login</label>
                <input className="ad-mono" value={identifiants?.sam_account_name || ""}
                       autoComplete="off"
                       onChange={(e) => majIdentifiant("sam_account_name", e.target.value)} />
                {ecartIdentifiant("sam_account_name")}
              </div>

              <div className="ad-champ">
                <label className="ad-label">UPN</label>
                <input className="ad-mono" value={identifiants?.upn || ""}
                       autoComplete="off"
                       onChange={(e) => majIdentifiant("upn", e.target.value)} />
                {ecartIdentifiant("upn")}
              </div>

              <div className="ad-champ">
                <label className="ad-label">Adresse principale</label>
                <input className="ad-mono" value={identifiants?.mail_principal || ""}
                       autoComplete="off"
                       onChange={(e) => majIdentifiant("mail_principal", e.target.value)} />
                {ecartIdentifiant("mail_principal")}
              </div>

              <dl className="ad-liste-def">
                <dt>Nom affiché</dt><dd>{calcul.nommage.display_name}</dd>
                {calcul.nommage.mail_nickname && (
                  <><dt>mailNickname</dt><dd>{calcul.nommage.mail_nickname}</dd></>
                )}
                <dt>OU cible</dt><dd className="ad-mono ad-petit">{calcul.ou_dn}</dd>
                {calcul.entite && (<><dt>Entité retenue</dt><dd>{calcul.entite}</dd></>)}
                {calcul.societe && (<><dt>Société</dt><dd>{calcul.societe}</dd></>)}
                {calcul.departement && (<><dt>Pôle</dt><dd>{calcul.departement}</dd></>)}
              </dl>

              <div className="ad-sous-bloc">
                <span className="ad-label">Adresses SMTP</span>
                {(identifiants?.proxy_addresses || []).map((p, i) => (
                  <input key={i} className="ad-mono ad-petit" value={p} autoComplete="off"
                         onChange={(e) => majProxy(i, e.target.value)} />
                ))}
                <p className="ad-aide">
                  Une seule entrée en SMTP: majuscules — c'est elle qui gouverne la boîte.
                </p>
                {calcul.nommage.target_address && (
                  <div className="ad-mono ad-petit">
                    targetAddress : {calcul.nommage.target_address}
                  </div>
                )}
              </div>

              {calcul.nommage.niveau_homonymie > 1 && (
                <div className="ad-message ad-message-info">
                  Homonymie détectée : identifiant étendu au niveau{" "}
                  {calcul.nommage.niveau_homonymie}.
                </div>
              )}
              {calcul.ou_dn_proposee && calcul.ou_dn_proposee !== calcul.ou_dn && (
                <div className="ad-message ad-message-info">
                  OU proposée par la correspondance : {calcul.ou_dn_proposee}
                </div>
              )}
              {calcul.entite_proposee && calcul.entite_proposee !== calcul.entite && (
                <div className="ad-message ad-message-info">
                  Entité habituelle de cette OU : {calcul.entite_proposee}.
                  Les adresses suivent l'entité retenue.
                </div>
              )}
              {calcul.nommage.divergence_odoo && (
                <div className="ad-message ad-message-info">
                  Écart avec Odoo — {calcul.nommage.divergence_odoo}
                </div>
              )}
            </div>

            <div className="v2-panneau">
              <h3 className="ad-section-titre">
                Groupes ({Object.values(groupesCoches).filter(Boolean).length} retenus
                sur {calcul.groupes.length})
              </h3>
              {calcul.groupes.length === 0 && (
                <p className="ad-aide">Aucun groupe ne correspond à ce profil.</p>
              )}
              {[3, 6, 8].map((etape) =>
                (groupesParEtape[etape] || []).length > 0 ? (
                  <div key={etape} className="ad-sous-bloc">
                    <span className="ad-label">
                      Étape {etape} — {LIBELLE_EXECUTEUR[groupesParEtape[etape][0].executeur]}
                    </span>
                    {groupesParEtape[etape].map((g) => (
                      <label key={g.nom}
                             className={`ad-groupe ${g.ajustable ? "" : "ad-groupe-verrouille"}`}>
                        <input type="checkbox" checked={!!groupesCoches[g.nom]}
                               disabled={!g.ajustable}
                               onChange={() => basculerGroupe(g)} />
                        <span className="ad-groupe-nom">{g.nom}</span>
                        {!g.ajustable && <span className="ad-badge">obligatoire</span>}
                        {g.sync_entra === false && <span className="ad-badge">no-sync</span>}
                        {g.commentaires.length > 0 && (
                          <span className="ad-aide-inline">{g.commentaires.join(" ; ")}</span>
                        )}
                      </label>
                    ))}
                  </div>
                ) : null
              )}
            </div>

            <div className="v2-panneau">
              <h3 className="ad-section-titre">Attributs</h3>
              <table className="ad-table">
                <tbody>
                  {calcul.attributs.map((a) => (
                    <tr key={a.nom_attribut} className={a.applique ? "" : "ad-inactif"}>
                      <td className="ad-mono ad-petit">{a.nom_attribut}</td>
                      <td>{a.applique ? (a.valeur || "—") : "non appliqué"}</td>
                      <td className="ad-aide">{a.motif || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {calcul.adresse && calcul.adresse.ville && (
                <div className="ad-sous-bloc">
                  <span className="ad-label">Adresse postale</span>
                  <div className="ad-petit">
                    {calcul.adresse.street_address}, {calcul.adresse.postal_code}{" "}
                    {calcul.adresse.ville} — {calcul.adresse.pays_libelle} (
                    {calcul.adresse.pays_iso})
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onglet : suivi des demandes
// ---------------------------------------------------------------------------

function SuiviDemandes({ collaborateur, rafraichissement }) {
  const [demandes, setDemandes] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [selection, setSelection] = useState(null);
  const [detail, setDetail] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setDemandes(await getDemandesAD(filtre || null));
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }, [filtre]);

  useEffect(() => { charger(); }, [charger, rafraichissement]);

  useEffect(() => {
    if (!selection) { setDetail(null); return; }
    getDemandeAD(selection).then(setDetail).catch((e) => setErreur(e.message));
  }, [selection]);

  const agir = async (action) => {
    try {
      const params = {
        collaborateur_id: collaborateur.id,
        validateur_upn: collaborateur.email || collaborateur.nom,
      };
      if (action === "valider") await validerDemandeAD(selection, params);
      else await annulerDemandeAD(selection, params);
      const maj = await getDemandeAD(selection);
      setDetail(maj);
      charger();
    } catch (e) {
      setErreur(e.message);
    }
  };

  return (
    <div className="ad-colonnes">
      <div className="v2-panneau">
        <div className="ad-barre-filtre">
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Tous les statuts</option>
            {Object.keys(LIBELLE_STATUT).map((s) => (
              <option key={s} value={s}>{LIBELLE_STATUT[s]}</option>
            ))}
          </select>
          <button className="v2-btn v2-btn-secondaire" onClick={charger} disabled={chargement}>
            {chargement ? "…" : "↺"}
          </button>
        </div>

        {erreur && <div className="ad-message ad-message-erreur">{erreur}</div>}

        <table className="ad-table">
          <thead>
            <tr>
              <th>Statut</th><th>Login</th><th>Entité</th><th>Origine</th><th>Créée le</th>
            </tr>
          </thead>
          <tbody>
            {demandes.map((d) => (
              <tr key={d.id} onClick={() => setSelection(d.id)}
                  className={`ad-ligne-cliquable ${selection === d.id ? "ad-ligne-active" : ""}`}>
                <td><span className={`ad-statut ad-statut-${d.statut}`}>
                  {LIBELLE_STATUT[d.statut] || d.statut}
                </span></td>
                <td className="ad-mono ad-petit">{d.sam_account_name}</td>
                <td>{d.entite}</td>
                <td>{d.source === "odoo" ? "Odoo" : "Interface"}</td>
                <td className="ad-petit">
                  {d.date_creation ? new Date(d.date_creation).toLocaleString("fr-FR") : ""}
                </td>
              </tr>
            ))}
            {demandes.length === 0 && !chargement && (
              <tr><td colSpan={5} className="ad-aide">Aucune demande.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="ad-resultat">
        {!detail && <div className="v2-panneau ad-vide">Sélectionne une demande pour voir son détail.</div>}

        {detail && (
          <>
            <div className="v2-panneau">
              <h3 className="ad-section-titre">Demande #{detail.id}</h3>
              <dl className="ad-liste-def">
                <dt>Statut</dt>
                <dd><span className={`ad-statut ad-statut-${detail.statut}`}>
                  {LIBELLE_STATUT[detail.statut] || detail.statut}
                </span></dd>
                <dt>Login</dt><dd className="ad-mono">{detail.sam_account_name}</dd>
                <dt>UPN</dt><dd className="ad-mono">{detail.upn}</dd>
                <dt>Demandeur</dt><dd>{detail.demandeur_upn || "—"}</dd>
                {detail.validateur_upn && (<><dt>Validé par</dt><dd>{detail.validateur_upn}</dd></>)}
              </dl>

              {detail.message_erreur && (
                <div className="ad-message ad-message-erreur">{detail.message_erreur}</div>
              )}
              {detail.nommage_divergence && (
                <div className="ad-message ad-message-info">
                  Écart Odoo — {detail.nommage_divergence}
                </div>
              )}

              <div className="ad-actions">
                {detail.statut === "en_attente_validation" && (
                  <button className="v2-btn v2-btn-principal" onClick={() => agir("valider")}>
                    Valider la demande
                  </button>
                )}
                {!["termine", "annule"].includes(detail.statut) && (
                  <button className="v2-btn v2-btn-secondaire" onClick={() => agir("annuler")}>
                    Annuler
                  </button>
                )}
              </div>
            </div>

            <div className="v2-panneau">
              <h3 className="ad-section-titre">Étapes</h3>
              <ol className="ad-etapes">
                {(detail.etapes || []).map((e) => (
                  <li key={e.id} className={`ad-etape ad-etape-${e.statut}`}>
                    <span className="ad-etape-num">{e.ordre}</span>
                    <div className="ad-etape-corps">
                      <div className="ad-etape-titre">
                        {LIBELLE_ETAPE[e.code_etape] || e.code_etape}
                        <span className="ad-badge">{LIBELLE_EXECUTEUR[e.executeur]}</span>
                        {e.tentatives > 1 && (
                          <span className="ad-badge">{e.tentatives} tentatives</span>
                        )}
                      </div>
                      {e.message && <div className="ad-petit">{e.message}</div>}
                      {e.prochaine_tentative && e.statut === "a_faire" && (
                        <div className="ad-aide">
                          Prochaine tentative :{" "}
                          {new Date(e.prochaine_tentative).toLocaleTimeString("fr-FR")}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="v2-panneau">
              <h3 className="ad-section-titre">Journal</h3>
              <table className="ad-table">
                <tbody>
                  {(detail.journal || []).map((j) => (
                    <tr key={j.id}>
                      <td className="ad-petit">
                        {new Date(j.horodatage).toLocaleString("fr-FR")}
                      </td>
                      <td className="ad-petit">{j.acteur}</td>
                      <td>
                        {j.action}
                        {j.statut_avant && j.statut_apres && (
                          <span className="ad-aide-inline">
                            {j.statut_avant} → {j.statut_apres}
                          </span>
                        )}
                      </td>
                      <td className="ad-aide">{j.message || ""}</td>
                    </tr>
                  ))}
                  {(detail.journal || []).length === 0 && (
                    <tr><td colSpan={4} className="ad-aide">Aucune entrée.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Onglet : demandes deposees par Odoo
//
// Etape de preparation uniquement. On lit le conteneur, on affiche la fiche,
// et on simule le calcul sans rien enregistrer. La creation reelle passera
// par POST /api/comptes-ad une fois les referentiels stabilises.
// ---------------------------------------------------------------------------

function DemandesOdoo({ collaborateur, referentiels }) {
  const [liste, setListe] = useState([]);
  const [container, setContainer] = useState("");
  const [typeFlux, setTypeFlux] = useState("");
  const [inclureTraitees, setInclureTraitees] = useState(false);
  const [selection, setSelection] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [ous, setOus] = useState([]);
  const [entite, setEntite] = useState("");
  const [ouDn, setOuDn] = useState("");
  const [etablissement, setEtablissement] = useState("");
  const [societe, setSociete] = useState("");
  const [departement, setDepartement] = useState("");
  // null = on laisse l'etablissement decider ; true/false = le technicien
  // s'est prononce et sa valeur prime.
  const [estAgence, setEstAgence] = useState(null);
  // L'alternance n'existe pas dans le referentiel de contrats Odoo : la
  // fonction peut dire "ALTERNANT(E)" en texte libre, rien d'exploitable.
  // C'est donc le technicien qui tranche.
  const [estAlternant, setEstAlternant] = useState(false);
  const [nomLangueLocale, setNomLangueLocale] = useState("");
  const [languePoste, setLanguePoste] = useState("FR");
  const [simulation, setSimulation] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [simulationEnCours, setSimulationEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const res = await getDemandesOdoo(inclureTraitees, typeFlux || null);
      setListe(res.demandes || []);
      setContainer(res.container || "");
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
      setListe([]);
    } finally {
      setChargement(false);
    }
  }, [inclureTraitees, typeFlux]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    getOUCiblesAD().then(setOus).catch(() => setOus([]));
  }, []);

  // Rafraichissement periodique : Odoo depose sans previr
  useEffect(() => {
    const timer = setInterval(charger, 10 * 60 * 1000);
    return () => clearInterval(timer);
  }, [charger]);

  const libelleOU = (o) => o.libelle;

  useEffect(() => {
    if (!selection) { setFiche(null); setSimulation(null); return; }
    setSimulation(null);
    getFicheOdoo(selection)
      .then((f) => {
        setFiche(f);

        // L'OU et l'entite decoulent du code de pole analytique via
        // Ref_Mapping_Odoo. Le backend fait la resolution ; on cherche ici
        // la correspondance uniquement pour pre-remplir les selecteurs.
        const ou = ous.find((o) => o.ou_dn === f.ou_mappee);
        setOuDn(ou ? ou.ou_dn : "");
        setEntite(ou ? ou.entite : "");

        const etabs = referentiels.etablissements || [];
        const trouve = etabs.find((e) => e.nom === f.etablissement);
        setEtablissement(trouve ? trouve.nom : "");

        // Societe et pole : ceux d'Odoo s'ils figurent au referentiel,
        // sinon vides et le backend deduira depuis le code de pole.
        setEstAgence(null);
        setEstAlternant(!!f.est_alternant);
        setNomLangueLocale("");
        setLanguePoste("FR");

        const socs = referentiels.societes || [];
        setSociete(socs.includes(f.societe) ? f.societe : "");
        const deps = referentiels.departements || [];
        setDepartement(deps.some((d) => d.libelle === f.departement)
                       ? f.departement : "");
      })
      .catch((e) => setErreur(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, ous]);

  const simuler = async () => {
    if (!fiche) return;
    if (!entite && !ouDn && !fiche.departement_code) {
      setErreur("Aucune correspondance : renseigne l'entité ou l'OU cible.");
      return;
    }
    setSimulationEnCours(true);
    setErreur(null);
    try {
      const res = await simulerCompteAD({
        prenom: fiche.prenom,
        nom: fiche.nom,
        entite: entite || null,
        ou_dn: ouDn || null,
        pole_code: fiche.departement_code || null,
        type_personnel: fiche.type_personnel || "Salarie",
        contrat: fiche.contrat || "CDI",
        est_alternant: estAlternant,
        etablissement: etablissement || null,
        company: societe || null,
        department: departement || null,
        projet: fiche.type_personnel === "Freelance" ? "projet" : null,
        // Absent de la charge utile quand le technicien ne s'est pas
        // prononce : le backend retient alors Ref_Etablissements.est_agence.
        est_agence: estAgence,
        nom_langue_locale: nomLangueLocale.trim() || null,
        langue_poste: languePoste,
      }, collaborateur.id);
      setSimulation(res);
    } catch (e) {
      setErreur(e.message);
      setSimulation(null);
    } finally {
      setSimulationEnCours(false);
    }
  };

  const ligneDef = (libelle, valeur) => (
    <>
      <dt>{libelle}</dt>
      <dd>{valeur || "—"}</dd>
    </>
  );

  return (
    <div className="ad-colonnes">
      {/* ---------------- Liste ---------------- */}
      <div className="v2-panneau">
        <h3 className="ad-section-titre">
          Demandes en attente{container ? ` — ${container}` : ""}
        </h3>

        <div className="ad-barre-filtre">
          <select value={typeFlux} onChange={(e) => setTypeFlux(e.target.value)}>
            <option value="">Tous les types</option>
            <option value="creation">Création</option>
            <option value="modification">Modification</option>
            <option value="desactivation">Désactivation</option>
          </select>
          <button className="v2-btn v2-btn-secondaire" onClick={charger} disabled={chargement}>
            {chargement ? "…" : "↺"}
          </button>
        </div>

        <label className="ad-case">
          <input type="checkbox" checked={inclureTraitees}
                 onChange={(e) => setInclureTraitees(e.target.checked)} />
          Afficher aussi les demandes déjà prises en charge
        </label>

        {erreur && <div className="ad-message ad-message-erreur">{erreur}</div>}

        <div className="ad-liste-defilante">
          <table className="ad-table">
            <thead>
              <tr>
                <th>Type</th><th>Matricule</th><th>Nom</th>
                <th>Société</th><th>Déposée le</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((d) => (
                <tr key={d.blob} onClick={() => setSelection(d.blob)}
                    className={`ad-ligne-cliquable ${selection === d.blob ? "ad-ligne-active" : ""}`}>
                  <td>
                    <span className={`ad-statut ad-statut-${d.type_flux === "creation" ? "termine" : "en_cours"}`}>
                      {d.type_flux === "creation" ? "Création"
                        : d.type_flux === "modification" ? "Modification"
                        : d.type_brut || "?"}
                    </span>
                    {d.deja_traitee && <span className="ad-badge">traitée</span>}
                    {d.erreur && <span className="ad-badge">illisible</span>}
                  </td>
                  <td className="ad-mono ad-petit">{d.source_ref || "—"}</td>
                  <td className="ad-petit">{d.display_fullname || d.blob}</td>
                  <td className="ad-petit">{d.societe || "—"}</td>
                  <td className="ad-petit">
                    {d.date_depot ? new Date(d.date_depot).toLocaleString("fr-FR") : "—"}
                  </td>
                </tr>
              ))}
              {liste.length === 0 && !chargement && (
                <tr><td colSpan={5} className="ad-aide">Aucune demande en attente.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="ad-aide">
          {liste.length} demande(s). Rafraîchissement automatique toutes les 10 minutes.
        </p>
      </div>

      {/* ---------------- Fiche ---------------- */}
      <div className="ad-resultat">
        {!fiche && (
          <div className="v2-panneau ad-vide">
            Sélectionne une demande pour voir sa fiche et simuler le calcul.
          </div>
        )}

        {fiche && fiche.erreur && (
          <div className="v2-panneau">
            <div className="ad-message ad-message-erreur">{fiche.erreur}</div>
            <p className="ad-aide ad-mono ad-petit">{fiche.blob}</p>
          </div>
        )}

        {fiche && !fiche.erreur && (
          <>
            <div className="v2-panneau">
              <h3 className="ad-section-titre">
                {fiche.display_fullname || `${fiche.prenom} ${fiche.nom}`}
              </h3>

              {fiche.deja_traitee && (
                <div className="ad-message ad-message-info">
                  Demande déjà prise en charge — demande #{fiche.demande_id},
                  statut {fiche.demande_statut}.
                </div>
              )}
              {fiche.type_flux !== "creation" && (
                <div className="ad-message ad-message-info">
                  Type « {fiche.type_brut} » : seul le flux de création est
                  implémenté pour l'instant. La simulation ci-dessous suppose
                  une création.
                </div>
              )}

              <dl className="ad-liste-def">
                {ligneDef("Matricule", fiche.source_ref)}
                {ligneDef("Prénom", fiche.prenom)}
                {ligneDef("Nom", fiche.nom)}
                {ligneDef("Fonction", fiche.titre)}
                {ligneDef("Société", fiche.societe)}
                {ligneDef("Pôle", fiche.departement)}
                {ligneDef("Code pôle", fiche.departement_code)}
                {ligneDef("Établissement", fiche.etablissement)}
                {ligneDef("Contrat", `${fiche.contrat_odoo || "—"}${fiche.est_alternant ? " (alternant)" : ""}`)}
                {ligneDef("Début", fiche.date_debut)}
                {ligneDef("Fin", fiche.date_fin)}
                {ligneDef("Manager", fiche.manager_nom)}
                {ligneDef("Demandeur", fiche.demandeur_nom)}
              </dl>

              <div className="ad-sous-bloc">
                <span className="ad-label">Adresse de l'établissement</span>
                <div className="ad-petit">
                  {[fiche.adresse?.street, fiche.adresse?.zip, fiche.adresse?.city,
                    fiche.adresse?.country].filter(Boolean).join(", ") || "—"}
                </div>
              </div>

              <div className="ad-sous-bloc">
                <span className="ad-label">Valeurs proposées par Odoo</span>
                <dl className="ad-liste-def">
                  {ligneDef("Login", <span className="ad-mono">{fiche.propositions_odoo?.samaccount_name}</span>)}
                  {ligneDef("UPN", <span className="ad-mono">{fiche.propositions_odoo?.email_groupe}</span>)}
                  {ligneDef("Adresse", <span className="ad-mono">{fiche.propositions_odoo?.email_company}</span>)}
                  {ligneDef("OU", <span className="ad-mono ad-petit">{fiche.ou_proposee}</span>)}
                </dl>
                <p className="ad-aide">
                  Ces valeurs servent de contrôle croisé : le calcul du backend fait autorité.
                </p>
              </div>

              {(fiche.logiciels || []).length > 0 && (
                <div className="ad-sous-bloc">
                  <span className="ad-label">Logiciels (hors périmètre du module)</span>
                  <div className="ad-petit">
                    {fiche.logiciels.map((l) => l.nom).join(", ")}
                  </div>
                </div>
              )}

              {(fiche.historique_demandes || []).length > 0 && (
                <div className="ad-sous-bloc">
                  <span className="ad-label">Demandes déjà enregistrées</span>
                  <table className="ad-table">
                    <tbody>
                      {fiche.historique_demandes.map((h) => (
                        <tr key={h.id}>
                          <td className="ad-petit">#{h.id}</td>
                          <td className="ad-petit">{h.type_flux}</td>
                          <td className="ad-petit">{h.statut}</td>
                          <td className="ad-mono ad-petit">{h.sam_account_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="v2-panneau">
              <h3 className="ad-section-titre">Simulation</h3>
              {fiche.departement_code && !fiche.ou_mappee && (
                <div className="ad-message ad-message-erreur">
                  Le pôle {fiche.departement_code} n'a pas de correspondance dans
                  le référentiel. Choisir manuellement l'OU cible.
                </div>
              )}
              {!fiche.departement_code && (
                <div className="ad-message ad-message-erreur">
                  Pôle sans code : aucune correspondance possible.
                  Choisir manuellement l'OU cible.
                </div>
              )}

              <div className="ad-champ">
                <label className="ad-label">OU cible</label>
                <select value={ouDn}
                        onChange={(e) => {
                          const v = e.target.value;
                          setOuDn(v);
                          const o = ous.find((x) => x.ou_dn === v);
                          if (o) setEntite(o.entite);
                          setSimulation(null);
                        }}>
                  <option value="">— Choisir —</option>
                  {ous.map((o) => (
                    <option key={o.ou_dn} value={o.ou_dn}>
                      {libelleOU(o)} — {o.entite}
                    </option>
                  ))}
                </select>
                {fiche.ou_mappee && (
                  <p className="ad-aide">
                    Déduite du pôle {fiche.departement_code}.
                  </p>
                )}
              </div>

              <div className="ad-grille-2">
                <div className="ad-champ">
                  <label className="ad-label">Entité</label>
                  <select value={entite}
                          onChange={(e) => { setEntite(e.target.value); setSimulation(null); }}>
                    <option value="">— Choisir —</option>
                    {(referentiels.entites || []).map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                  <p className="ad-aide">Gouverne les adresses. Indépendante de l'OU.</p>
                </div>
                <div className="ad-champ">
                  <label className="ad-label">Établissement</label>
                  <select value={etablissement}
                          onChange={(e) => { setEtablissement(e.target.value); setSimulation(null); }}>
                    <option value="">— Choisir —</option>
                    {(referentiels.etablissements || []).map((e) => (
                      <option key={e.nom} value={e.nom}>{e.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ad-grille-2">
                <div className="ad-champ">
                  <label className="ad-label">Société contractuelle</label>
                  <select value={societe}
                          onChange={(e) => { setSociete(e.target.value); setSimulation(null); }}>
                    <option value="">— Déduite du pôle —</option>
                    {(referentiels.societes || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="ad-champ">
                  <label className="ad-label">Pôle analytique</label>
                  <select value={departement}
                          onChange={(e) => { setDepartement(e.target.value); setSimulation(null); }}>
                    <option value="">— Déduit du code —</option>
                    {(referentiels.departements || []).map((d) => (
                      <option key={d.code} value={d.libelle}>{d.libelle}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ad-grille-2">
                <div className="ad-champ">
                  <label className="ad-label">Langue du poste</label>
                  <select value={languePoste}
                          onChange={(e) => { setLanguePoste(e.target.value);
                                             setSimulation(null); }}>
                    {(referentiels.langues_poste || ["FR", "ENG"]).map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <div className="ad-champ">
                  <label className="ad-label">Nom en langue locale</label>
                  <input value={nomLangueLocale} autoComplete="off"
                         placeholder="Chine, Japon, Corée uniquement"
                         onChange={(e) => { setNomLangueLocale(e.target.value);
                                            setSimulation(null); }} />
                </div>
              </div>

              <div className="ad-cases">
                <label className="ad-case">
                  <input type="checkbox" checked={estAlternant}
                         onChange={(e) => { setEstAlternant(e.target.checked);
                                            setSimulation(null); }} />
                  Alternant
                  <span className="ad-aide-inline">
                    Absent du référentiel Odoo — à cocher manuellement.
                  </span>
                </label>

                <label className="ad-case">
                  <input type="checkbox"
                         checked={estAgence === null
                                  ? !!(simulation && simulation.est_agence)
                                  : estAgence}
                         onChange={(e) => { setEstAgence(e.target.checked);
                                            setSimulation(null); }} />
                  Présent à l'agence
                  <span className="ad-aide-inline">
                    Déduit de l'établissement — ajustable.
                  </span>
                </label>
              </div>

              <div className="ad-actions">
                <button className="v2-btn v2-btn-principal" onClick={simuler}
                        disabled={simulationEnCours}>
                  {simulationEnCours ? "Calcul en cours…" : "Simuler"}
                </button>
              </div>
            </div>

            {simulation && (
              <>
                <div className="v2-panneau">
                  <h3 className="ad-section-titre">Identifiants calculés</h3>
                  <dl className="ad-liste-def">
                    <dt>Login</dt><dd className="ad-mono">{simulation.nommage.sam_account_name}</dd>
                    <dt>UPN</dt><dd className="ad-mono">{simulation.nommage.upn}</dd>
                    <dt>Adresse</dt><dd className="ad-mono">{simulation.nommage.mail_principal}</dd>
                    <dt>Nom affiché</dt><dd>{simulation.nommage.display_name}</dd>
                    {simulation.nommage.mail_nickname && (
                      <>
                        <dt>mailNickname</dt>
                        <dd>{simulation.nommage.mail_nickname}</dd>
                      </>
                    )}
                    <dt>OU cible</dt><dd className="ad-mono ad-petit">{simulation.ou_dn}</dd>
                    {simulation.entite && (
                      <><dt>Entité retenue</dt><dd>{simulation.entite}</dd></>
                    )}
                    {simulation.societe && (
                      <><dt>Société</dt><dd>{simulation.societe}</dd></>
                    )}
                    {simulation.departement && (
                      <><dt>Pôle</dt><dd>{simulation.departement}</dd></>
                    )}
                  </dl>

                  <div className="ad-sous-bloc">
                    <span className="ad-label">Adresses SMTP</span>
                    {simulation.nommage.proxy_addresses.map((p) => (
                      <div key={p} className="ad-mono ad-petit">{p}</div>
                    ))}
                    {/* targetAddress n'est renseigne que pour Heaven : c'est lui
                        qui route le courrier vers la boite cible. */}
                    {simulation.nommage.target_address && (
                      <div className="ad-mono ad-petit">
                        targetAddress : {simulation.nommage.target_address}
                      </div>
                    )}
                  </div>

                  {simulation.nommage.niveau_homonymie > 1 && (
                    <div className="ad-message ad-message-info">
                      Homonymie détectée : identifiant étendu au niveau{" "}
                      {simulation.nommage.niveau_homonymie}.
                    </div>
                  )}
                  {fiche.ou_proposee && simulation.ou_dn !== fiche.ou_proposee && (
                    <div className="ad-message ad-message-info">
                      L'OU retenue diffère de celle proposée par Odoo
                      ({fiche.ou_proposee}).
                    </div>
                  )}
                </div>

                <div className="v2-panneau">
                  <h3 className="ad-section-titre">
                    Groupes proposés ({simulation.groupes.filter((g) => g.coche).length}
                    {" "}sur {simulation.groupes.length})
                  </h3>
                  {simulation.groupes.length === 0 && (
                    <p className="ad-aide">Aucun groupe ne correspond à ce profil.</p>
                  )}
                  {simulation.groupes.map((g) => (
                    <div key={g.nom} className={`ad-groupe ${g.coche ? "" : "ad-inactif"}`}>
                      <span className="ad-groupe-nom">{g.nom}</span>
                      <span className="ad-badge">étape {g.etape}</span>
                      {!g.ajustable && <span className="ad-badge">obligatoire</span>}
                    </div>
                  ))}
                </div>

                <div className="v2-panneau">
                  <h3 className="ad-section-titre">Attributs</h3>
                  <table className="ad-table">
                    <tbody>
                      {simulation.attributs.map((a) => (
                        <tr key={a.nom_attribut} className={a.applique ? "" : "ad-inactif"}>
                          <td className="ad-mono ad-petit">{a.nom_attribut}</td>
                          <td>{a.applique ? (a.valeur || "—") : "non appliqué"}</td>
                          <td className="ad-aide">{a.motif || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onglet : administration des referentiels
//
// Les formulaires sont generes a partir de la description renvoyee par
// /api/comptes-ad-admin/tables : ajouter une colonne cote backend suffit,
// aucune modification n'est necessaire ici.
// ---------------------------------------------------------------------------

function AdminReferentiels({ collaborateur }) {
  const [tables, setTables] = useState({});
  const [cle, setCle] = useState("regles");
  const [lignes, setLignes] = useState([]);
  const [options, setOptions] = useState({});
  const [valeurs, setValeurs] = useState({});
  const [recherche, setRecherche] = useState("");
  const [filtre2, setFiltre2] = useState("");
  const [valeursFiltre2, setValeursFiltre2] = useState([]);
  const [inactifs, setInactifs] = useState(false);
  const [edition, setEdition] = useState(null); // { id, valeurs } — id null = creation
  const [historique, setHistorique] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  const config = tables[cle];

  useEffect(() => {
    getTablesAdminAD().then(setTables).catch((e) => setErreur(e.message));
    Promise.all([getOptionsAdminAD("entites"), getOptionsAdminAD("groupes")])
      .then(([entites, groupes]) => setOptions({ entites, groupes }))
      .catch((e) => setErreur(e.message));

    // Listes de valeurs des criteres, chargees une fois pour toutes
    const cles = ["entites", "societes", "departements", "contrats",
                  "etablissements", "pays", "villes", "types_personnel"];
    Promise.all(cles.map((c) => getValeursAdminAD(c).catch(() => [])))
      .then((res) => {
        const map = {};
        cles.forEach((c, i) => { map[c] = res[i]; });
        setValeurs(map);
      });
  }, []);

  const charger = useCallback(async () => {
    if (!cle) return;
    setChargement(true);
    try {
      setLignes(await getLignesAdminAD(cle, inactifs, recherche || null, filtre2 || null));
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [cle, inactifs, recherche, filtre2]);

  useEffect(() => { charger(); }, [charger]);

  // Valeurs du second filtre : rechargees a chaque changement de referentiel
  useEffect(() => {
    if (!cle) return;
    getValeursFiltreAdminAD(cle)
      .then(setValeursFiltre2)
      .catch(() => setValeursFiltre2([]));
  }, [cle]);

  const changerReferentiel = (nouvelle) => {
    setCle(nouvelle);
    setEdition(null);
    setHistorique(null);
    setRecherche("");
    setFiltre2("");
    setSucces(null);
    setErreur(null);
  };

  const nouvelleLigne = () => {
    const valeurs = {};
    (config?.colonnes || []).forEach((c) => {
      if (c.type === "multi") valeurs[c.nom] = [];
      else if (c.type === "bool") valeurs[c.nom] = c.nom === "actif";
      else valeurs[c.nom] = "";
    });
    if (valeurs.actif !== undefined) valeurs.actif = true;
    setEdition({ id: null, valeurs });
    setHistorique(null);
  };

  const ouvrirLigne = (ligne) => {
    const valeurs = {};
    (config?.colonnes || []).forEach((c) => {
      const v = ligne[c.nom];
      if (c.type === "multi") {
        valeurs[c.nom] = Array.isArray(v) ? v : (v ? [v] : []);
      } else {
        valeurs[c.nom] = v === null || v === undefined ? "" : v;
      }
    });
    setEdition({ id: ligne.id, valeurs });
    setHistorique(null);
    setSucces(null);
  };

  const majValeur = (nom, valeur) => {
    setEdition((e) => ({ ...e, valeurs: { ...e.valeurs, [nom]: valeur } }));
  };

  // Echap ferme la fiche : une modale sans sortie au clavier devient vite
  // penible quand on enchaine les corrections de referentiel.
  useEffect(() => {
    if (!edition) return;
    const surTouche = (e) => { if (e.key === "Escape") setEdition(null); };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [edition]);

  const enregistrer = async () => {
    setErreur(null);
    try {
      if (edition.id === null) {
        await creerLigneAdminAD(cle, edition.valeurs, collaborateur.id);
        setSucces("Ligne créée.");
      } else {
        await modifierLigneAdminAD(cle, edition.id, edition.valeurs, collaborateur.id);
        setSucces("Ligne mise à jour.");
      }
      setEdition(null);
      charger();
    } catch (e) {
      setErreur(e.message);
    }
  };

  const basculerActivation = async () => {
    if (!edition || edition.id === null) return;
    try {
      if (edition.valeurs.actif) {
        await desactiverLigneAdminAD(cle, edition.id, collaborateur.id);
        setSucces("Ligne désactivée.");
      } else {
        await reactiverLigneAdminAD(cle, edition.id, collaborateur.id);
        setSucces("Ligne réactivée.");
      }
      setEdition(null);
      charger();
    } catch (e) {
      setErreur(e.message);
    }
  };

  const voirHistorique = async () => {
    if (!edition || edition.id === null) return;
    try {
      setHistorique(await getHistoriqueAdminAD(cle, edition.id));
    } catch (e) {
      setErreur(e.message);
    }
  };

  // Affiche la valeur d'une colonne dans le tableau de synthese
  const afficher = (ligne, nom) => {
    const colonne = (config?.colonnes || []).find((c) => c.nom === nom);
    const valeur = ligne[nom];
    if (valeur === null || valeur === undefined || valeur === "") return "—";
    if (colonne?.ref) {
      const liste = options[colonne.ref] || [];
      const trouve = liste.find((o) => String(o.id) === String(valeur));
      return trouve ? trouve.libelle : `#${valeur}`;
    }
    if (Array.isArray(valeur)) {
      if (valeur.length === 0) return "—";
      const liste = valeurs[colonne?.ref_valeurs] || [];
      const libelles = valeur.map((v) => {
        const t = liste.find((x) => String(x.valeur) === String(v));
        return t ? t.libelle : v;
      });
      return libelles.length > 3
        ? `${libelles.slice(0, 3).join(", ")} +${libelles.length - 3}`
        : libelles.join(", ");
    }
    if (colonne?.ref_valeurs) {
      const liste = valeurs[colonne.ref_valeurs] || [];
      const trouve = liste.find((v) => String(v.valeur) === String(valeur));
      return trouve ? trouve.libelle : String(valeur);
    }
    if (colonne?.type === "bool" || colonne?.type === "bool_nullable") {
      return valeur ? "Oui" : "Non";
    }
    return String(valeur);
  };

  const libelleFiltre2 = () => {
    if (!config?.filtre2) return null;
    const c = (config.colonnes || []).find((x) => x.nom === config.filtre2);
    return c ? c.libelle : config.filtre2;
  };

  const champ = (colonne) => {
    const valeur = edition.valeurs[colonne.nom];

    if (colonne.ref) {
      const liste = options[colonne.ref] || [];
      return (
        <select value={valeur ?? ""} onChange={(e) => majValeur(colonne.nom, e.target.value)}>
          <option value="">— Choisir —</option>
          {liste.map((o) => (
            <option key={o.id} value={o.id}>{o.libelle}</option>
          ))}
        </select>
      );
    }

    // Critere multivalue : entre criteres c'est un ET, au sein d'un
    // critere un OU. Aucune case cochee vaut "indifferent".
    if (colonne.type === "multi") {
      const liste = valeurs[colonne.ref_valeurs] || [];
      const choisies = Array.isArray(valeur) ? valeur : (valeur ? [valeur] : []);
      const basculer = (v) => {
        const suivant = choisies.includes(v)
          ? choisies.filter((x) => x !== v)
          : [...choisies, v];
        majValeur(colonne.nom, suivant);
      };
      return (
        <div className="ad-multi">
          {choisies.length === 0 && (
            <p className="ad-aide">Aucune valeur : critère indifférent.</p>
          )}
          <div className="ad-multi-liste">
            {liste.map((v) => (
              <label key={v.valeur} className="ad-case">
                <input type="checkbox" checked={choisies.includes(v.valeur)}
                       onChange={() => basculer(v.valeur)} />
                {v.libelle}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (colonne.ref_valeurs) {
      const liste = valeurs[colonne.ref_valeurs] || [];
      return (
        <select value={valeur ?? ""} onChange={(e) => majValeur(colonne.nom, e.target.value)}>
          <option value="">— Indifférent —</option>
          {liste.map((v) => (
            <option key={v.valeur} value={v.valeur}>{v.libelle}</option>
          ))}
        </select>
      );
    }

    if (colonne.type === "select") {
      return (
        <select value={valeur ?? ""} onChange={(e) => majValeur(colonne.nom, e.target.value)}>
          {(colonne.options || []).map((o) => (
            <option key={o} value={o}>{o === "" ? "— Indifférent —" : o}</option>
          ))}
        </select>
      );
    }

    if (colonne.type === "bool") {
      return (
        <label className="ad-case">
          <input type="checkbox" checked={!!valeur}
                 onChange={(e) => majValeur(colonne.nom, e.target.checked)} />
          {valeur ? "Oui" : "Non"}
        </label>
      );
    }

    if (colonne.type === "bool_nullable") {
      const courant = valeur === "" || valeur === null || valeur === undefined
        ? "" : (valeur ? "1" : "0");
      return (
        <select value={courant} onChange={(e) => majValeur(colonne.nom, e.target.value)}>
          <option value="">— Indifférent —</option>
          <option value="1">Oui</option>
          <option value="0">Non</option>
        </select>
      );
    }

    if (colonne.type === "textarea") {
      return (
        <textarea rows={colonne.rows || 3} value={valeur ?? ""}
                  onChange={(e) => majValeur(colonne.nom, e.target.value)} />
      );
    }

    return (
      <input type={colonne.type === "int" ? "number" : "text"}
             value={valeur ?? ""} autoComplete="off"
             onChange={(e) => majValeur(colonne.nom, e.target.value)} />
    );
  };

  return (
    <>
      {/* ---------------- Liste, pleine largeur ---------------- */}
      <div className="v2-panneau">
        <div className="ad-admin-barre">
          <div className="ad-champ">
            <label className="ad-label">Référentiel</label>
            <select value={cle} onChange={(e) => changerReferentiel(e.target.value)}>
              {Object.keys(tables).map((k) => (
                <option key={k} value={k}>{tables[k].libelle}</option>
              ))}
            </select>
          </div>

          {config?.filtre2 && (
            <div className="ad-champ">
              <label className="ad-label">Filtrer par {libelleFiltre2()}</label>
              <select value={filtre2} onChange={(e) => setFiltre2(e.target.value)}>
                <option value="">— Tous —</option>
                {valeursFiltre2.map((v) => (
                  <option key={v.id} value={v.id}>{v.libelle}</option>
                ))}
              </select>
            </div>
          )}

          <div className="ad-champ">
            <label className="ad-label">Recherche</label>
            <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
                   placeholder="Rechercher…" autoComplete="off" />
          </div>

          <div className="ad-admin-actions">
            <button className="v2-btn v2-btn-secondaire" onClick={charger} disabled={chargement}>
              {chargement ? "…" : "↺"}
            </button>
            <button className="v2-btn v2-btn-principal" onClick={nouvelleLigne} disabled={!config}>
              + Nouvelle
            </button>
          </div>
        </div>

        {config && <p className="ad-aide">{config.description}</p>}

        <label className="ad-case">
          <input type="checkbox" checked={inactifs}
                 onChange={(e) => setInactifs(e.target.checked)} />
          Afficher aussi les lignes désactivées
        </label>

        {erreur && <div className="ad-message ad-message-erreur">{erreur}</div>}
        {succes && <div className="ad-message ad-message-succes">{succes}</div>}

        <div className="ad-liste-defilante">
          <table className="ad-table">
            <thead>
              <tr>
                {(config?.resume || []).map((nom) => {
                  const c = (config.colonnes || []).find((x) => x.nom === nom);
                  return <th key={nom}>{c ? c.libelle : nom}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id} onClick={() => ouvrirLigne(l)}
                    className={`ad-ligne-cliquable ${l.actif ? "" : "ad-inactif"}`}>
                  {(config?.resume || []).map((nom) => (
                    <td key={nom} className="ad-petit">{afficher(l, nom)}</td>
                  ))}
                </tr>
              ))}
              {lignes.length === 0 && !chargement && (
                <tr>
                  <td colSpan={(config?.resume || []).length || 1} className="ad-aide">
                    Aucune ligne.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="ad-aide">{lignes.length} ligne(s)</p>
      </div>

      {/* ---------------- Fiche en modale ---------------- */}
      {edition && config && (
        <div className="ad-modale-fond" onClick={() => setEdition(null)}>
          {/* stopPropagation : un clic dans la fiche ne doit pas la fermer */}
          <div className="ad-modale" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modale-entete">
              <h3 className="ad-section-titre">
                {edition.id === null ? `Nouvelle ligne — ${config.libelle}`
                                     : `${config.libelle} #${edition.id}`}
              </h3>
              <button className="ad-modale-fermer" onClick={() => setEdition(null)}
                      title="Fermer">×</button>
            </div>

            <div className="ad-modale-corps">
              {config.colonnes.map((c) => (
                <div key={c.nom} className="ad-champ">
                  <label className="ad-label">
                    {c.libelle}{c.req ? " *" : ""}
                  </label>
                  {champ(c)}
                </div>
              ))}

              {historique && (
                <div className="ad-sous-bloc">
                  <span className="ad-label">Historique des versions</span>
                  <table className="ad-table">
                    <thead>
                      <tr><th>Depuis</th><th>Jusqu'à</th><th>Auteur</th></tr>
                    </thead>
                    <tbody>
                      {historique.map((h, i) => (
                        <tr key={i}>
                          <td className="ad-petit">
                            {new Date(h.valid_from).toLocaleString("fr-FR")}
                          </td>
                          <td className="ad-petit">
                            {new Date(h.valid_to).getFullYear() > 9000
                              ? "actuelle"
                              : new Date(h.valid_to).toLocaleString("fr-FR")}
                          </td>
                          <td className="ad-petit">{h.modifie_par || "—"}</td>
                        </tr>
                      ))}
                      {historique.length === 0 && (
                        <tr><td colSpan={3} className="ad-aide">
                          Aucune version antérieure.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="ad-modale-pied">
              <button className="v2-btn v2-btn-secondaire" onClick={() => setEdition(null)}>
                Annuler
              </button>
              {edition.id !== null && (
                <button className="v2-btn v2-btn-secondaire" onClick={voirHistorique}>
                  Historique
                </button>
              )}
              {edition.id !== null && (
                <button className="v2-btn v2-btn-secondaire" onClick={basculerActivation}>
                  {edition.valeurs.actif ? "Désactiver" : "Réactiver"}
                </button>
              )}
              <button className="v2-btn v2-btn-principal" onClick={enregistrer}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Pages v2
// ---------------------------------------------------------------------------

function EnteteAD({ titre, description, children }) {
  return (
    <div className="v2-bib-entete">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">{titre}</h1>
        <p className="v2-sous-titre">{description}</p>
      </header>
      {children}
    </div>
  );
}

// Referentiels communs a Odoo et Nouvelle demande (charges une fois)
function useReferentielsAD() {
  const [referentiels, setReferentiels] = useState({});
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    getReferentielsAD()
      .then(setReferentiels)
      .catch((e) => setErreur(e.message));
  }, []);
  return { referentiels, erreur };
}

// Creation : onglets Odoo (en premier : la quasi-totalite des demandes
// viendra de l'ERP) et Nouvelle demande
export function CreationAD({ collaborateur }) {
  const [onglet, setOnglet] = useState("odoo");
  const { referentiels, erreur } = useReferentielsAD();

  return (
    <div className="v2-page v2-page-module v2-ad">
      <EnteteAD
        titre="Création"
        description="Les demandes sont mises en file : le compte est créé par l'agent on-premises, puis synchronisé vers Entra."
      >
        <div className="v2-bascule" role="tablist" aria-label="Origine de la demande">
          <button
            type="button"
            role="tab"
            aria-selected={onglet === "odoo"}
            className={`v2-bascule-choix${onglet === "odoo" ? " actif" : ""}`}
            onClick={() => setOnglet("odoo")}
          >
            Odoo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={onglet === "nouvelle"}
            className={`v2-bascule-choix${onglet === "nouvelle" ? " actif" : ""}`}
            onClick={() => setOnglet("nouvelle")}
          >
            Nouvelle demande
          </button>
        </div>
      </EnteteAD>

      {erreur && <div className="ad-message ad-message-erreur">{erreur}</div>}

      <div role="tabpanel">
        {onglet === "odoo" && <DemandesOdoo collaborateur={collaborateur} referentiels={referentiels} />}
        {onglet === "nouvelle" && (
          <NouvelleDemande collaborateur={collaborateur} referentiels={referentiels} onCree={() => {}} />
        )}
      </div>
    </div>
  );
}

// Historique : suivi des demandes, etapes et journal
export function HistoriqueAD({ collaborateur }) {
  return (
    <div className="v2-page v2-page-module v2-ad">
      <EnteteAD titre="Historique" description="Suivi des demandes : statut, étapes d'exécution et journal." />
      <SuiviDemandes collaborateur={collaborateur} rafraichissement={0} />
    </div>
  );
}

// Gestion : administration des referentiels (directeurs, comme avant)
export function GestionAD({ collaborateur }) {
  if (collaborateur?.role !== "directeur") return null;
  return (
    <div className="v2-page v2-page-module v2-ad">
      <EnteteAD
        titre="Gestion"
        description="Administration des référentiels : entités, OU, sociétés, pôles, contrats, établissements, groupes et règles."
      />
      <AdminReferentiels collaborateur={collaborateur} />
    </div>
  );
}
